import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import type { LegacyMatch, LegacyTournament } from "../api/legacyTypes";
import { usePreferences } from "../app/preferencesContext";
import { gameModule } from "../games/registry";
import { usePlayerSummaries } from "../hooks/usePlayerSummaries";
import { dateTimeSortValue } from "../utils/dateTime";
import { isUnseenResult, markResultSeen, markResultsSeen, readResultHistory, resultHistoryStorageKey, resultSnapshot, writeResultHistory } from "../utils/resultHistory";
import { GameBadge } from "./GameBadge";
import { CounterStrikeMatchPreview } from "./CounterStrikeMatchPreview";
import { DotaMatchPreview } from "./DotaMatchPreview";
import { LocalDateTime } from "./LocalDateTime";
import { MatchContext } from "./MatchContext";
import { ResultParticipants } from "./ResultParticipants";

interface FeedResult {
  id: string;
  tournament: LegacyTournament;
  match: LegacyMatch;
  initiallyUnseen: boolean;
}

function resultEntries(tournaments: LegacyTournament[]): FeedResult[] {
  return tournaments.flatMap((tournament) => {
    const previous = readResultHistory(tournament.id);
    return tournament.matches
      .filter((match) => match.status === "ongoing" || match.status === "completed")
      .map((match) => ({
        id: `${tournament.id}:${match.sourceKey}`,
        tournament,
        match,
        initiallyUnseen: match.status === "completed" && isUnseenResult(previous, match),
      }));
  }).sort((left, right) => {
    const statusDifference = Number(right.match.status === "ongoing") - Number(left.match.status === "ongoing");
    return statusDifference || dateTimeSortValue(right.match.startsAt) - dateTimeSortValue(left.match.startsAt) || left.id.localeCompare(right.id);
  });
}

function ResultFeedCard({ entry, hidden, revealed, onReveal }: { entry: FeedResult; hidden: boolean; revealed: boolean; onReveal: () => void }) {
  const { tournament, match } = entry;
  const [preview, setPreview] = useState(false);
  const game = gameModule(tournament.wiki);
  const summaries = usePlayerSummaries(tournament.wiki, tournament.matches).data ?? {};
  const tournamentURL = `/tournaments/${tournament.wiki}/${tournament.id.split("/").at(-1)}`;
  const tournamentFeedURL = `/?tournament=${encodeURIComponent(tournament.id)}`;
  return <article
    className={["result-feed-card", match.status === "ongoing" ? "result-feed-live" : "", hidden ? "result-feed-hidden" : "", revealed ? "result-feed-revealed" : ""].filter(Boolean).join(" ")}
    data-feed-result={entry.id}
    data-game={tournament.wiki}
    style={{ "--game-accent": game?.accent ?? "var(--fp-accent)" } as CSSProperties}
  >
    <header>
      <div className="result-feed-context">
        <GameBadge gameId={tournament.wiki} />
        <a href={tournamentFeedURL}>{tournament.name}</a>
        {match.status === "ongoing" && <span className="match-status-live">Live</span>}
      </div>
      <div className="result-feed-actions"><MatchContext match={match} allMatches={tournament.matches} /><a className="result-feed-open" href={tournamentURL}>Tournament ↗</a></div>
    </header>
    <div className="result-feed-body"><ResultParticipants gameId={tournament.wiki} match={match} summaries={summaries} /></div>
    <footer>
      <LocalDateTime value={match.startsAt} status={match.status} />
      {revealed && <span className="quick-result-revealed-label">Revealed</span>}
      {!hidden && (tournament.wiki === "dota2" || tournament.wiki === "counterstrike") && <button className="match-stats-trigger" type="button" onClick={() => setPreview(true)}>Quick stats</button>}
    </footer>
    {hidden && <button type="button" className="result-feed-reveal" onClick={onReveal}>Reveal result</button>}
    {preview && tournament.wiki === "dota2" && <DotaMatchPreview match={match} tournamentId={tournament.id} onClose={() => setPreview(false)} />}
    {preview && tournament.wiki === "counterstrike" && <CounterStrikeMatchPreview match={match} tournamentId={tournament.id} onClose={() => setPreview(false)} />}
  </article>;
}

export function ResultsFeed({ tournaments }: { tournaments: LegacyTournament[] }) {
  const { antiSpoiler } = usePreferences();
  const [entries] = useState(() => resultEntries(tournaments));
  const [historyRevision, setHistoryRevision] = useState(0);
  const oldestUnseenIndex = useMemo(() => entries.reduce((last, entry, index) => entry.initiallyUnseen ? index : last, -1), [entries]);
  const initialVisibleCount = Math.min(entries.length, Math.max(10, oldestUnseenIndex + 2));
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const anchorRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const userHasScrolled = useRef(false);
  const anchorKey = oldestUnseenIndex >= 0 ? entries[oldestUnseenIndex + 1]?.id ?? "after-all" : "";
  const visibleEntries = entries.slice(0, visibleCount);
  const remaining = entries.length - visibleCount;
  const liveCount = entries.filter((entry) => entry.match.status === "ongoing").length;
  const hiddenEntryIds = (() => {
    // A history event increments this revision so the storage-backed calculation
    // below is rerun without changing the feed's fixed ordering.
    void historyRevision;
    if (!antiSpoiler) return new Set<string>();
    const histories = new Map<string, ReturnType<typeof readResultHistory>>();
    return new Set(entries.filter((entry) => {
      if (!histories.has(entry.tournament.id)) histories.set(entry.tournament.id, readResultHistory(entry.tournament.id));
      return isUnseenResult(histories.get(entry.tournament.id) ?? null, entry.match);
    }).map((entry) => entry.id));
  })();
  const visibleHiddenEntries = visibleEntries.filter((entry) => hiddenEntryIds.has(entry.id));

  useEffect(() => {
    tournaments.forEach((tournament) => {
      if (readResultHistory(tournament.id) === null) writeResultHistory(tournament.id, resultSnapshot(tournament.matches));
    });
  }, [tournaments]);

  useEffect(() => {
    const tournamentIds = new Set(tournaments.map((tournament) => tournament.id));
    const resultListener = (event: Event) => {
      if (tournamentIds.has((event as CustomEvent<string>).detail)) setHistoryRevision((current) => current + 1);
    };
    const storageKeys = new Set(tournaments.map((tournament) => resultHistoryStorageKey(tournament.id)));
    const storageListener = (event: StorageEvent) => {
      if (event.key && storageKeys.has(event.key)) setHistoryRevision((current) => current + 1);
    };
    window.addEventListener("fp:results-changed", resultListener);
    window.addEventListener("storage", storageListener);
    return () => {
      window.removeEventListener("fp:results-changed", resultListener);
      window.removeEventListener("storage", storageListener);
    };
  }, [tournaments]);

  useEffect(() => {
    if (!anchorKey) return;
    const frame = window.requestAnimationFrame(() => {
      const anchor = anchorRef.current;
      if (anchor && typeof anchor.scrollIntoView === "function") anchor.scrollIntoView({ block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [anchorKey]);

  useEffect(() => {
    const markScrolled = () => { userHasScrolled.current = true; };
    const markKeyboardScroll = (event: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "End", "Home", " "].includes(event.key)) markScrolled();
    };
    window.addEventListener("wheel", markScrolled, { passive: true });
    window.addEventListener("touchmove", markScrolled, { passive: true });
    window.addEventListener("keydown", markKeyboardScroll);
    return () => {
      window.removeEventListener("wheel", markScrolled);
      window.removeEventListener("touchmove", markScrolled);
      window.removeEventListener("keydown", markKeyboardScroll);
    };
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((records) => {
      if (userHasScrolled.current && records.some((record) => record.isIntersecting)) setVisibleCount((current) => Math.min(entries.length, current + 10));
    }, { rootMargin: "0px 0px 60px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [entries.length, visibleCount]);

  const reveal = (entry: FeedResult) => {
    setRevealed((current) => new Set(current).add(entry.id));
    markResultSeen(entry.tournament.id, entry.match);
  };

  const revealAllShown = () => {
    const byTournament = new Map<string, LegacyMatch[]>();
    for (const entry of visibleHiddenEntries) byTournament.set(entry.tournament.id, [...(byTournament.get(entry.tournament.id) ?? []), entry.match]);
    for (const [tournamentId, matches] of byTournament) markResultsSeen(tournamentId, matches);
    setRevealed((current) => new Set([...current, ...visibleHiddenEntries.map((entry) => entry.id)]));
  };

  if (entries.length === 0) return <p className="state-panel">No ongoing matches or completed results in this feed yet.</p>;

  return <section className="results-feed" aria-label="Results feed">
    <div className="results-feed-summary">
      <div><strong>{liveCount > 0 ? `${liveCount} live · ` : ""}{hiddenEntryIds.size} unseen</strong>{visibleHiddenEntries.length > 0 && <button className="results-feed-reveal-all" type="button" onClick={revealAllShown}>Reveal all shown ({visibleHiddenEntries.length})</button>}</div>
      <span>Live matches first · newest results after · entries never move when revealed</span>
    </div>
    <div className="results-feed-list">
      {visibleEntries.map((entry) => <div key={entry.id}>
        {entry.id === anchorKey && <div className="last-seen-divider" ref={anchorRef}><span>Last seen</span></div>}
        <ResultFeedCard entry={entry} hidden={hiddenEntryIds.has(entry.id)} revealed={revealed.has(entry.id)} onReveal={() => reveal(entry)} />
      </div>)}
      {anchorKey === "after-all" && <div className="last-seen-divider" ref={anchorRef}><span>Last seen</span></div>}
    </div>
    {remaining > 0 && <button ref={loadMoreRef} type="button" className="results-feed-more" onClick={() => setVisibleCount((current) => Math.min(entries.length, current + 10))}>Load 10 older results · {remaining} remaining</button>}
  </section>;
}
